import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { HomePage } from "./pages/HomePage";

// Eager pages — small + critical.
// Lazy pages — code-split out of the initial bundle so HomePage paints fast.
// Each lazy() chunk is fetched only when its route is visited.
const SearchPage         = lazy(() => import("./pages/SearchPage").then(m => ({ default: m.SearchPage })));
const MainPage           = lazy(() => import("./pages/MainPage").then(m => ({ default: m.MainPage })));
const AuthPage           = lazy(() => import("./pages/AuthPage").then(m => ({ default: m.AuthPage })));
const AdminPanel         = lazy(() => import("./pages/AdminPanel").then(m => ({ default: m.AdminPanel })));
const ClubPartnerPage    = lazy(() => import("./pages/ClubPartnerPage"));
const VoucherDetailPage  = lazy(() => import("./pages/VoucherDetailPage"));
const TermsOfService     = lazy(() => import("./pages/TermsOfService").then(m => ({ default: m.TermsOfService })));
const PrivacyPolicy      = lazy(() => import("./pages/PrivacyPolicy").then(m => ({ default: m.PrivacyPolicy })));
const ForBusiness        = lazy(() => import("./pages/ForBusiness").then(m => ({ default: m.ForBusiness })));
const ContactPage        = lazy(() => import("./pages/ContactPage").then(m => ({ default: m.ContactPage })));
const NotificationsPage  = lazy(() => import("./pages/NotificationsPage"));
const FavoritesPage      = lazy(() => import("./pages/FavoritesPage"));

import { AuthProvider, useAuth } from "./context/AuthContext";
import { GoogleOAuthProvider } from '@react-oauth/google';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
import { SiteSettingsProvider, useSiteSettings } from "./context/SiteSettingsContext";
import { ThemeProvider } from "./context/ThemeContext";
import { NotificationProvider } from "./context/NotificationContext";
import { FavoritesProvider } from "./context/FavoritesContext";
import { usePushSubscription } from "./hooks/usePushSubscription";
import { DesktopTopNav } from "./components/DesktopTopNav";
import { Toaster } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get or create device ID
const getDeviceId = () => {
  let deviceId = localStorage.getItem('pricemap_device_id');
  if (!deviceId) {
    deviceId = 'device_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    localStorage.setItem('pricemap_device_id', deviceId);
  }
  return deviceId;
};

// PWA Installation Tracker
function PWAInstallTracker() {
  useEffect(() => {
    const deviceId = getDeviceId();
    
    // Track when PWA is installed
    const handleAppInstalled = async () => {
      console.log('[PWA] App installed!');
      try {
        await fetch(`${API_URL}/api/track-pwa-install`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            device_id: deviceId,
            platform: navigator.platform || 'unknown',
            user_agent: navigator.userAgent,
            standalone: window.matchMedia('(display-mode: standalone)').matches
          })
        });
      } catch (e) {
        console.log('[PWA] Failed to track install:', e);
      }
    };
    
    // Track if app is running as PWA (already installed)
    const checkStandaloneMode = async () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
        || window.navigator.standalone 
        || document.referrer.includes('android-app://');
      
      if (isStandalone) {
        // Check if we already tracked this device's PWA launch
        const tracked = localStorage.getItem('pwa_launch_tracked');
        if (!tracked) {
          console.log('[PWA] Running in standalone mode');
          try {
            await fetch(`${API_URL}/api/track-pwa-launch`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                device_id: deviceId,
                platform: navigator.platform || 'unknown',
                user_agent: navigator.userAgent
              })
            });
            localStorage.setItem('pwa_launch_tracked', 'true');
          } catch (e) {
            console.log('[PWA] Failed to track launch:', e);
          }
        }
      }
    };
    
    // Listen for install event
    window.addEventListener('appinstalled', handleAppInstalled);
    
    // Check if running as PWA on load
    checkStandaloneMode();
    
    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);
  
  return null;
}

// Web Push subscriber — registers logged-in user's device for push
// notifications (chat replies reach the phone even when the app is closed).
function PushSubscriber() {
  usePushSubscription();
  return null;
}

// Track page visits
function VisitTracker() {
  const location = useLocation();
  const { user } = useAuth();
  
  useEffect(() => {
    // Track visit on route change
    const trackVisit = async () => {
      try {
        await fetch(`${API_URL}/api/track-visit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            path: location.pathname,
            user_id: user?.id || null
          })
        });
      } catch (e) {
        // Silently ignore tracking errors
      }
    };
    trackVisit();
  }, [location.pathname, user?.id]);
  
  return null;
}

// Loading spinner component
function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{backgroundColor: '#fffcf7'}}>
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent"></div>
    </div>
  );
}

// Protected Route wrapper - only for admin
function AdminRoute({ children }) {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) return <LoadingSpinner />;
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

// Redirects /club/:cat → /?cat=X so old links and back-nav from
// ClubPartnerPage land on the voucher feed filtered to that category.
function ClubCatRedirect() {
  const { cat } = useParams();
  return <Navigate to={`/?cat=${encodeURIComponent(cat)}`} replace />;
}

function AppRoutes() {
  const { settings } = useSiteSettings();
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    }>
    <Routes>
      {/* Main page - PUBLIC, accessible to all (guest mode enabled) */}
      <Route path="/" element={<HomePage />} />

      {/* [Sprint 1 redesign] Two new public pages — price search and
          PriceMap Club coupons. Both pieces of the redesigned 3-page
          shell. /club/:cat/:slug remains the deep-link partner detail
          page (registered below behind the show_club_section toggle). */}
      <Route path="/search" element={<SearchPage />} />
      {/* [Sprint UI-7] "მთავარი" tab: slogan + two big tabs. The voucher
          catalogue lives on "/" (ფასდაკლებები tab). */}
      <Route path="/discounts" element={<MainPage />} />
      <Route path="/voucher/:offerId" element={<VoucherDetailPage />} />
      <Route path="/notifications" element={<NotificationsPage />} />
      <Route path="/favorites" element={<FavoritesPage />} />
      
      {/* Auth page */}
      <Route path="/auth" element={<AuthPage />} />
      
      {/* Secret admin login - always accessible */}
      <Route path="/pm-admin" element={<AuthPage adminAccess={true} />} />
      
      {/* Admin Panel - protected, admin only */}
      <Route 
        path="/admin" 
        element={
          <AdminRoute>
            <AdminPanel />
          </AdminRoute>
        } 
      />
      
      {/* Legal pages - accessible without login */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/for-business" element={<ForBusiness />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/contact" element={<ContactPage />} />

      {/* [Sprint C8.2 C2/C3b] PriceMap Club — integrated entry point.
          Homepage chips link to /club/{cat}, partner cards drill down
          to /club/{cat}/{slug}. Both routes are gated by the master
          show_club_section toggle (post-C4): when admin disables the
          feature, the chip section is hidden on home AND any direct
          /club/* navigation redirects home so disabled means disabled.
          React Router's <Routes> needs Route children directly — no
          Fragments — so we list the three conditionals separately. */}
      {settings.show_club_section && (
        <Route path="/club/:cat" element={<ClubCatRedirect />} />
      )}
      {settings.show_club_section && (
        <Route path="/club/:cat/:slug" element={<ClubPartnerPage />} />
      )}
      {!settings.show_club_section && (
        <Route path="/club/*" element={<Navigate to="/" replace />} />
      )}
    </Routes>
    </Suspense>
  );
}

// [Sprint C3 deploy window] Maintenance mode with URL-parameter bypass.
//   - Default: show MaintenancePage to everyone
//   - Bypass: /?preview=davidtest2026 → sets localStorage flag → full site
//   - Revoke: change BYPASS_SECRET or flip MAINTENANCE_ENABLED to false
//
// When the deploy window is over, set MAINTENANCE_ENABLED = false and redeploy
// (or just delete this block and restore the original App() body).
const MAINTENANCE_ENABLED = false;
const BYPASS_SECRET = 'pm-dev-2026-04-23-xk7p';
const BYPASS_STORAGE_KEY = 'pricemap_maintenance_bypass';

function MaintenancePage() {
  return (
    <div style={{
      margin: 0,
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      background: 'linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      color: '#1e293b',
    }}>
      <div style={{
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center',
        background: 'white',
        padding: '40px 30px',
        borderRadius: '20px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
      }}>
        <div style={{ fontSize: '64px', marginBottom: '20px' }}>🛠️</div>
        <h1 style={{ fontSize: '26px', margin: '0 0 16px', lineHeight: 1.3 }}>
          ვებ გვერდზე მიდის განახლება
        </h1>
        <p style={{ color: '#64748b', fontSize: '16px', lineHeight: 1.6, margin: '0 0 24px' }}>
          მალე დავბრუნდებით.
        </p>
        <div style={{
          display: 'inline-block',
          background: '#fef3c7',
          color: '#92400e',
          padding: '10px 20px',
          borderRadius: '999px',
          fontWeight: 600,
          fontSize: '14px',
        }}>
          ⏱️ მცირე ხანში ხელმისაწვდომი ვიქნებით
        </div>
        <div style={{
          marginTop: '32px',
          paddingTop: '24px',
          borderTop: '1px solid #e2e8f0',
          color: '#94a3b8',
          fontSize: '13px',
          lineHeight: 1.6,
        }}>
          <div style={{ fontWeight: 700, color: '#f97316', fontSize: '14px', marginBottom: '4px' }}>
            PriceMap.ge
          </div>
          <div>ფასები და ფასდაკლებები</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Returns true when the user has bypass access (either via the URL secret
 * or via a previously-saved localStorage flag).
 *
 * On URL match, we persist the flag so refreshes stay authenticated and
 * also strip ?preview= from the URL (cleaner history, prevents the secret
 * from leaking via referrer headers).
 */
function hasMaintenanceBypass() {
  try {
    const params = new URLSearchParams(window.location.search);
    const preview = params.get('preview');
    if (preview === BYPASS_SECRET) {
      localStorage.setItem(BYPASS_STORAGE_KEY, BYPASS_SECRET);
      params.delete('preview');
      const clean = window.location.pathname + (params.toString() ? `?${params}` : '') + window.location.hash;
      window.history.replaceState({}, '', clean);
      return true;
    }
    return localStorage.getItem(BYPASS_STORAGE_KEY) === BYPASS_SECRET;
  } catch {
    return false;
  }
}

function App() {
  if (MAINTENANCE_ENABLED && !hasMaintenanceBypass()) {
    return <MaintenancePage />;
  }

  return (
    <div className="App">
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <ThemeProvider>
          <AuthProvider>
            <SiteSettingsProvider>
              <NotificationProvider>
                <FavoritesProvider>
                  <BrowserRouter>
                    <DesktopTopNav />
                    <PWAInstallTracker />
                    <VisitTracker />
                    <PushSubscriber />
                    <AppRoutes />
                  </BrowserRouter>
                  <Toaster position="top-center" richColors />
                </FavoritesProvider>
              </NotificationProvider>
            </SiteSettingsProvider>
          </AuthProvider>
        </ThemeProvider>
      </GoogleOAuthProvider>
    </div>
  );
}

export default App;
